import type { CardTemplate } from '@molt/shared';
import { Eye, EyeOff, LayoutTemplate } from 'lucide-react';
import type { ReactElement } from 'react';

import { Button } from '@/components/Button';
import { cn } from '@/lib/cn';
import { useInterfaceLanguage } from '@/lib/interface-language';

export interface TemplateCardVisibilityActionProps {
  readonly template: Pick<CardTemplate, 'id' | 'title' | 'hiddenFromList'>;
  readonly canManage: boolean;
  readonly disabledReason: string | null;
  readonly busy?: boolean | undefined;
  readonly onHide: (cardId: string) => void;
  readonly onUnhide: (cardId: string) => void;
  readonly className?: string;
}

export function TemplateCardVisibilityAction({
  template,
  canManage,
  disabledReason,
  busy,
  onHide,
  onUnhide,
  className,
}: TemplateCardVisibilityActionProps): ReactElement {
  const { t } = useInterfaceLanguage();
  const title =
    template.title.trim() === ''
      ? t('boardTemplates.cardVisibility.untitledTemplate')
      : template.title;
  const hidden = template.hiddenFromList;
  const label = hidden
    ? t('boardTemplates.cardVisibility.show')
    : t('boardTemplates.cardVisibility.hide');
  const accessibleName = hidden
    ? t('boardTemplates.cardVisibility.showName', { title })
    : t('boardTemplates.cardVisibility.hideName', { title });
  const reason = canManage
    ? null
    : (disabledReason ?? t('boardTemplates.cardVisibility.reason'));
  const busyProps = busy === undefined ? {} : { busy };
  const disabledProps = reason === null ? {} : { disabled: true, disabledReason: reason };

  return (
    <div
      className={cn(
        'flex items-center justify-between gap-3 rounded-card border border-border bg-surface px-3 py-2',
        className,
      )}
    >
      <span className="inline-flex min-w-0 items-center gap-2 text-sm text-ink">
        <LayoutTemplate aria-hidden="true" className="size-4 shrink-0 text-accent" />
        <span className="min-w-0 truncate">
          {hidden
            ? t('boardTemplates.cardVisibility.hiddenLabel')
            : t('boardTemplates.cardVisibility.label')}
        </span>
      </span>
      <Button
        type="button"
        size="sm"
        variant="secondary"
        aria-label={accessibleName}
        iconStart={
          hidden ? (
            <Eye aria-hidden="true" className="size-4" />
          ) : (
            <EyeOff aria-hidden="true" className="size-4" />
          )
        }
        {...busyProps}
        {...disabledProps}
        onClick={() => {
          if (hidden) {
            onUnhide(template.id);
          } else {
            onHide(template.id);
          }
        }}
      >
        {label}
      </Button>
    </div>
  );
}
